import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import "@/App.css";

import { CartProvider } from "@/context/CartContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AnnouncementBar from "@/components/AnnouncementBar";
import Header from "@/components/Header";
import HeroSlider from "@/components/HeroSlider";
import CategoryGrid from "@/components/CategoryGrid";
import DealsCarousel from "@/components/DealsCarousel";
import ProductGrid from "@/components/ProductGrid";
import TrustBadges from "@/components/TrustBadges";
import Testimonials from "@/components/Testimonials";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import VoiceOrderButton from "@/components/VoiceOrderButton";
import LoginDialog from "@/components/LoginDialog";
import { GoogleProfileDialog } from "@/components/GoogleProfileDialog";
import FloatingWhatsAppButton from "@/components/FloatingWhatsAppButton";
import AssistantChat from "@/components/AssistantChat";
import ProductDetailDialog from "@/components/ProductDetailDialog";
import BrandFilter from "@/components/BrandFilter";
import { useBackClose } from "@/lib/backstack";

const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const AdminApp = lazy(() => import("@/admin/AdminApp"));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Shop() {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeBrand, setActiveBrand] = useState(null);
  const { loginOpen, setLoginOpen, openLogin } = useAuth();
  const [detailProduct, setDetailProduct] = useState(null);

  useEffect(() => {
    let alive = true;
    try {
      const cached = JSON.parse(localStorage.getItem("ae_shop_v2") || "null");
      if (cached) {
        setBanners(cached.banners || []);
        setCategories(cached.categories || []);
        setProducts(cached.products || []);
        setDeals(cached.deals || []);
      }
    } catch (e) {
      console.warn("Could not read cached shop data:", e);
    }
    (async () => {
      try {
        const [b, c, p, d] = await Promise.all([
          axios.get(`${API}/banners`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/products`),
          axios.get(`${API}/products`, { params: { deals: true } }),
        ]);
        if (!alive) return;
        const data = {
          banners: b.data || [],
          categories: c.data || [],
          products: p.data || [],
          deals: (d.data || []).slice(0, 8),
        };
        setBanners(data.banners);
        setCategories(data.categories);
        setProducts(data.products);
        setDeals(data.deals);
        try {
          localStorage.setItem("ae_shop_v2", JSON.stringify(data));
        } catch (e) {
          console.warn("Could not cache shop data:", e);
        }
      } catch (e) {
        console.error("Failed to load shop data", e);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filteredProducts = useMemo(() => {
    let list = products;
    if (activeCategory) list = list.filter((p) => p.category_slug === activeCategory.slug);
    if (activeBrand) list = list.filter((p) => p.brand === activeBrand);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q));
    }
    return list;
  }, [products, activeCategory, activeBrand, search]);

  // For brand pills inside the active category: per-brand product counts.
  const brandCountsInCategory = useMemo(() => {
    if (!activeCategory) return {};
    const counts = {};
    for (const p of products) {
      if (p.category_slug !== activeCategory.slug) continue;
      counts[p.brand] = (counts[p.brand] || 0) + 1;
    }
    return counts;
  }, [products, activeCategory]);

  const allProductsTitle = useMemo(() => {
    if (search) return `Results for "${search}"`;
    if (activeCategory && activeBrand) return `${activeBrand} · ${activeCategory.name}`;
    if (activeCategory) return activeCategory.name;
    if (activeBrand) return activeBrand;
    return "Shop All Products";
  }, [search, activeCategory, activeBrand]);

  const scrollToProducts = () => {
    document.getElementById("all-products")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Hardware/browser Back returns to the unfiltered home view instead of closing the app.
  useBackClose(!!(activeCategory || search || activeBrand), () => {
    setActiveCategory(null);
    setSearch("");
    setActiveBrand(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  });

  const openDetails = (p) => {
    setDetailProduct(p);
    if ((p.variant_count ?? 0) > 0 && !(p.variants || []).length) {
      axios
        .get(`${API}/products/${p.id}`)
        .then((r) => setDetailProduct((cur) => (cur && cur.id === p.id ? r.data : cur)))
        .catch(() => {});
    }
  };
  const closeDetails = () => setDetailProduct(null);

  return (
    <div className="App">
      <AnnouncementBar />
      <Header
        onLoginClick={openLogin}
        onSearch={(q) => { setSearch(q); setActiveCategory(null); scrollToProducts(); }}
      />

      <HeroSlider banners={banners} />

      <CategoryGrid
        categories={categories}
        onSelect={(c) => {
          setActiveCategory((curr) => (curr?.slug === c.slug ? null : c));
          setSearch("");
          setActiveBrand(null);
          scrollToProducts();
        }}
      />

      <DealsCarousel products={deals} onOpenDetails={openDetails} />

      <div id="all-products">
        {activeCategory && (
          <BrandFilter
            brands={activeCategory.brands || []}
            active={activeBrand}
            onSelect={(b) => setActiveBrand(b)}
            productCounts={brandCountsInCategory}
          />
        )}

        {(activeCategory || search || activeBrand) && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setActiveCategory(null); setSearch(""); setActiveBrand(null); }}
              className="text-xs font-bold text-[#0056D2] hover:underline"
            >
              ← Clear filters
            </button>
            {activeCategory && (
              <span className="text-xs px-2 py-1 rounded-full bg-[#0056D2]/10 text-[#0056D2] font-bold">
                {activeCategory.name}
              </span>
            )}
            {activeBrand && (
              <span className="text-xs px-2 py-1 rounded-full bg-[#FF5722]/10 text-[#FF5722] font-bold">
                Brand: {activeBrand}
              </span>
            )}
            {search && (
              <span className="text-xs px-2 py-1 rounded-full bg-[#FFD700] text-gray-900 font-bold">&ldquo;{search}&rdquo;</span>
            )}
          </div>
        )}

        <ProductGrid
          products={filteredProducts}
          eyebrow={activeBrand ? "Brand" : activeCategory ? "Category" : search ? "Search" : "All Products"}
          title={allProductsTitle}
          subtitle={`${filteredProducts.length} product${filteredProducts.length === 1 ? "" : "s"} from India\u2019s top brands`}
          onOpenDetails={openDetails}
        />
      </div>

      <TrustBadges />
      <Testimonials />
      <Footer />

      <CartDrawer />
      <VoiceOrderButton />
      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
      />
      <GoogleProfileDialog />
      <FloatingWhatsAppButton />
      <AssistantChat />
      <ProductDetailDialog
        product={detailProduct}
        open={!!detailProduct}
        onClose={closeDetails}
      />

      {/* Quiet admin link */}
      <Link
        to="/admin"
        className="fixed bottom-3 left-3 text-[10px] text-gray-400 hover:text-gray-700 z-30 font-mono"
        data-testid="admin-entry-link"
      >
        admin
      </Link>
    </div>
  );
}

function StorefrontWithProvider() {
  // ScrollToTop on navigation
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return (
    <AuthProvider>
      <CartProvider>
        <Shop />
      </CartProvider>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="min-h-screen grid place-items-center text-sm font-bold text-gray-400">Loading…</div>}>
        <Routes>
          <Route path="/" element={<StorefrontWithProvider />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

// re-export for tests if needed
export { Shop };
// silence unused import warning
void useNavigate;
